'use client';

import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import { BBox } from 'geojson';

type MapViewportOptions = {
  padding?: number;
};

// Helper function to check if bbox values have significantly changed
function hasSignificantBboxChange(oldBbox: BBox, newBbox: BBox, threshold = 0.001): boolean {
  return (
    Math.abs(oldBbox[0] - newBbox[0]) > threshold ||
    Math.abs(oldBbox[1] - newBbox[1]) > threshold ||
    Math.abs(oldBbox[2] - newBbox[2]) > threshold ||
    Math.abs(oldBbox[3] - newBbox[3]) > threshold
  );
}

export const DEFAULT_VIEWPORT_PADDING = 0;

export function useMapViewport({ padding = DEFAULT_VIEWPORT_PADDING }: MapViewportOptions = {}) {
  const map = useMap();
  const [bbox, setBbox] = useState<BBox>([-180, -90, 180, 90]);
  const [zoom, setZoom] = useState(0);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastBboxRef = useRef<BBox>([-180, -90, 180, 90]);
  const lastZoomRef = useRef<number>(0);

  // observe the map to get current bounds
  useEffect(() => {
    if (!map) return;

    const updateViewport = () => {
      // Clear any pending update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Throttle updates to avoid excessive re-renders
      updateTimeoutRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        const currentZoom = map.getZoom();
        const projection = map.getProjection();

        if (!bounds || !currentZoom || !projection) return;

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const paddingDegrees = degreesPerPixel(currentZoom) * padding;

        const n = Math.min(90, ne.lat() + paddingDegrees);
        const s = Math.max(-90, sw.lat() - paddingDegrees);
        const w = sw.lng() - paddingDegrees;
        const e = ne.lng() + paddingDegrees;

        const newBbox: BBox = [w, s, e, n];

        // Only update if there's a significant change in bbox or zoom
        const zoomChanged = Math.abs(lastZoomRef.current - currentZoom) >= 0.1;
        const bboxChanged = hasSignificantBboxChange(lastBboxRef.current, newBbox);

        if (zoomChanged) {
          setZoom(currentZoom);
          lastZoomRef.current = currentZoom;
        }

        if (bboxChanged) {
          setBbox(newBbox);
          lastBboxRef.current = newBbox;
        }
      }, 150); // Throttle updates to 150ms
    };

    // Update immediately if map is ready
    if (map.getBounds() && map.getZoom()) {
      updateViewport();
    }

    const listener = map.addListener('idle', updateViewport);

    return () => {
      listener.remove();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [map, padding]);

  return { bbox, zoom };
}

function degreesPerPixel(zoomLevel: number) {
  // 360° divided by the number of pixels at the zoom-level
  return 360 / (Math.pow(2, zoomLevel) * 256);
}
